import DesignButton from 'components/button/DesignButton';
import DesignCard from 'components/card/DesignCard';
import DesignSelect from 'components/select/DesignSelect';
import InfoBox from 'components/infoBox/InfoBox';
import KpiCard from 'components/kpiCard/KpiCard';
import { AlertIcon, InfoIcon, SpinnerIcon } from 'components/svgs';
import { GenericTable, TableModel } from 'components/table/IdmTable';
import { useCapacityPlanRoutesQuery } from 'core/graphql/CapacityPlanning/CapacityPlanRoutes.generated';
import {
  CapacityPlanRunDetail,
  DepotSummaryView,
  SolveView,
  UnassignedStopView,
} from 'core/graphql/CapacityPlanning/models';
import {
  CapacityPlanRunStatus,
  CapacityPlanSolveStatus,
} from 'core/graphql/types';
import { formatDateTime, formatTime } from 'core/utils/DateUtils';
import { timeSpanToHhMm, timeSpanToSeconds } from 'core/utils/TimeSpanUtils';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { CapacityPlanTab } from './CapacityPlanTab';
import {
  bucketCounts,
  recipientsPerDriverText,
  summarizeCounts,
} from './perDriver';
import type { PerDriverBucket } from './perDriver';
import {
  depotLabel,
  depotSummaryTableModel,
  depotTotalsRow,
  toDepotSummaryRows,
} from './DepotSummaryTableConfiguration';
import DriverRouteDialog from './DriverRouteDialog';
import ResultMap from './ResultMap';
import { routesTableModel, toRouteRows } from './RoutesTableConfiguration';
import {
  isRunSolving,
  outOfCountryExcludedNote,
  solveProgressLabelOf,
} from './runProgress';
import useCapacityPlanUrl from './useCapacityPlanUrl';

export interface ResultTabProps {
  run: CapacityPlanRunDetail;
}

const subtleText = 'text-light-black dark:text-sublte-white';

/**
 * The retry is a run-level action and lives in the run header, next to Compute,
 * so this tab names it rather than offering a second button of its own.
 */
export const retryHint =
  'Use Retry failed solves in the run header to put them back on the queue.';

const sectionHeading = 'text-xl font-bold';

const runReference = (run: CapacityPlanRunDetail) => run.id.slice(0, 8);

const shiftOf = (run: CapacityPlanRunDetail) =>
  `${timeSpanToHhMm(run.settings.shiftStart)}–${timeSpanToHhMm(
    run.settings.shiftEnd,
  )}`;

/** Shift Window length in seconds, for the routes table's "Shift used" column. */
const shiftSecondsOf = (run: CapacityPlanRunDetail) => {
  const start = timeSpanToSeconds(run.settings.shiftStart);
  const end = timeSpanToSeconds(run.settings.shiftEnd);
  return start === null || end === null ? null : end - start;
};

/** "Depot 2 (44.43, 26.10) — 12,340 deliveries" */
const depotOptionLabel = (depot: DepotSummaryView) =>
  `${depotLabel(depot)} (${depot.latitude.toFixed(
    2,
  )}, ${depot.longitude.toFixed(
    2,
  )}) — ${depot.deliveries.toLocaleString()} deliveries`;

/**
 * Unassigned stops, grouped the way the solver reports them: a reason, how many
 * deliveries it costs and which parcels to look at. A DeliveryId is the parcel's
 * own code from the file; no customer data is joined in here.
 */
const unassignedTableModel: TableModel<UnassignedStopView> = {
  columns: [
    {
      title: 'Reason',
      align: 'left',
      width: 6,
      html: (row) => <span>{row.reason}</span>,
    },
    {
      title: 'Deliveries',
      align: 'right',
      width: 2,
      html: (row) => <span>{row.deliveryIds.length}</span>,
    },
    {
      title: 'Delivery IDs',
      align: 'left',
      width: 4,
      html: (row) => (
        <span className="flex flex-wrap gap-1">
          {row.deliveryCodes.map((deliveryCode) => (
            <span
              key={deliveryCode}
              className="rounded-sm bg-muted-white px-1.5 py-0.5 text-xs dark:bg-design-black-accent"
            >
              {deliveryCode}
            </span>
          ))}
        </span>
      ),
    },
  ],
};

/** Queued or Computing: nothing to show yet, so the panel explains the wait. */
const ProgressPanel = ({ run }: { run: CapacityPlanRunDetail }) => {
  const isQueued = run.status === CapacityPlanRunStatus.Queued;
  const startedAt = new Date(
    (isQueued ? run.queuedAt : run.computingStartedAt) ?? run.createdAt,
  );

  return (
    <div className="flex flex-col gap-4">
      <DesignCard className="flex flex-col items-center gap-2 py-10 text-center">
        <SpinnerIcon className="h-8 w-8 text-yellow" />
        <h3 className={sectionHeading}>
          {isQueued ? 'Waiting in the queue' : 'Computing the plan'}
        </h3>
        <p className="text-sm font-semibold">{solveProgressLabelOf(run)}</p>
        <p className={`text-sm ${subtleText}`}>
          Started {formatTime(startedAt)} · {run.validDeliveries} deliveries,
          capacity {run.settings.capacity ?? 'No limit'}, shift {shiftOf(run)}
        </p>
        <p className={`text-xs ${subtleText}`}>
          This page refreshes on its own. You can leave — the run stays in the
          link.
        </p>
      </DesignCard>
    </div>
  );
};

/** What the supervisor can do about a failed solve, without leaving the tab. */
const FailedPanel = ({
  run,
  onOpenTab,
}: {
  run: CapacityPlanRunDetail;
  onOpenTab: (tab: CapacityPlanTab) => void;
}) => {
  const error = run.error ?? 'No details available.';

  const handleCopyError = async () => {
    if (!navigator.clipboard) {
      toast.error('Copying is not available in this browser.');
      return;
    }
    try {
      await navigator.clipboard.writeText(error);
      toast.success('The error was copied.');
    } catch {
      toast.error('The error could not be copied.');
    }
  };

  const checks: { text: string; label: string; onClick: () => void }[] = [
    {
      text: 'Coordinates of the listed stops — open Deliveries and set them by hand',
      label: 'Open Deliveries',
      onClick: () => onOpenTab('deliveries'),
    },
    {
      text: 'Shift window long enough for the farthest stops',
      label: 'Open Settings',
      onClick: () => onOpenTab('settings'),
    },
    {
      text: 'Depot inside the service area',
      label: 'Open Settings',
      onClick: () => onOpenTab('settings'),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <InfoBox
        variant="danger"
        icon={<AlertIcon />}
        title="The computation failed"
        action={
          <DesignButton
            variant="ghost"
            size="sm"
            onClickHandler={handleCopyError}
          >
            Copy error
          </DesignButton>
        }
      >
        <div>{error}</div>
        <div className="mt-1 text-xs">
          Reported {formatDateTime(new Date(run.computedAt ?? run.createdAt))} ·
          run {runReference(run)}
        </div>
        {run.solveProgress.failed > 0 && (
          <div className="mt-1 text-xs">{retryHint}</div>
        )}
      </InfoBox>

      <DesignCard title="What to check">
        <ul className="flex flex-col gap-2">
          {checks.map((check) => (
            <li key={check.text} className="flex items-center gap-3 text-sm">
              <span
                aria-hidden="true"
                className="h-1.5 w-1.5 shrink-0 rounded-full bg-dots-grey"
              />
              <span className="flex-grow">{check.text}</span>
              <DesignButton
                variant="ghost"
                size="sm"
                onClickHandler={check.onClick}
              >
                {check.label}
              </DesignButton>
            </li>
          ))}
        </ul>
      </DesignCard>
    </div>
  );
};

const failedSolveCountLabel = (count: number) =>
  count === 1 ? '1 solve failed' : `${count} solves failed`;

/**
 * Failed Solves of the Run: the selected depot lists its own, and a Network Run
 * says when the failures sit on a depot that is not open. The retry itself is
 * offered by the run header, so it is reachable from every tab.
 */
const FailedSolves = ({
  runFailedCount,
  solves,
  failuresAreOnAnotherDepot,
}: {
  runFailedCount: number;
  solves: SolveView[];
  failuresAreOnAnotherDepot: boolean;
}) => (
  <InfoBox
    variant="danger"
    icon={<AlertIcon />}
    title={
      solves.length > 0
        ? `${solves.length} of this depot's solves failed`
        : failedSolveCountLabel(runFailedCount)
    }
  >
    {solves.length > 0 && (
      <ul className="list-inside list-disc">
        {solves.map((solve) => (
          <li key={solve.id}>
            Cluster {solve.clusterIndex + 1}: {solve.error ?? 'No details.'}
          </li>
        ))}
      </ul>
    )}
    {solves.length === 0 && failuresAreOnAnotherDepot && (
      <p>They belong to another planning depot.</p>
    )}
    <div className="mt-1 text-xs">{retryHint}</div>
  </InfoBox>
);

/** Horizontal bar histogram of drivers per bucket; nothing for no buckets. */
const PerDriverHistogram = ({ buckets }: { buckets: PerDriverBucket[] }) => {
  if (buckets.length === 0) {
    return null;
  }
  const maxDrivers = Math.max(1, ...buckets.map((b) => b.drivers));

  return (
    <div className="mt-4 flex flex-col gap-1.5">
      {buckets.map((b) => (
        <div key={`${b.from}-${b.to}`} className="flex items-center gap-2">
          <span className={`w-14 shrink-0 text-xs ${subtleText}`}>
            {b.from}–{b.to}
          </span>
          <div className="h-3 flex-grow overflow-hidden rounded-sm bg-design-secondary-black">
            <div
              className="h-3 rounded-sm bg-bright-blue"
              style={{
                width: `${(b.drivers / maxDrivers) * 100}%`,
              }}
            />
          </div>
          <span className="w-6 shrink-0 text-right text-xs">{b.drivers}</span>
        </div>
      ))}
    </div>
  );
};

/** Min / median / max / average of `values` per driver, with its histogram. */
const PerDriverCard = ({
  title,
  noun,
  values,
}: {
  title: string;
  noun: string;
  values: number[];
}) => {
  const { min, median, max, average } = summarizeCounts(values);
  const stat = (label: string, value: number | null) => (
    <div>
      <div
        aria-label={`${label} ${noun} per driver`}
        className="text-3xl font-bold"
      >
        {value ?? '-'}
      </div>
      <div className={`text-xs ${subtleText}`}>{label}</div>
    </div>
  );

  return (
    <DesignCard title={title}>
      <div className="grid grid-cols-3 gap-4">
        {stat('Min', min)}
        {stat('Median', median)}
        {stat('Max', max)}
      </div>
      <p
        aria-label={`Average ${noun} per driver`}
        className={`mt-1 text-xs ${subtleText}`}
      >
        Average {average === null ? '-' : average.toFixed(1)}
      </p>
      <PerDriverHistogram buckets={bucketCounts(values)} />
    </DesignCard>
  );
};

/** The computed plan: totals, the depots, and one depot's routes at a time. */
const ComputedResult = ({
  run,
  onOpenTab,
}: {
  run: CapacityPlanRunDetail;
  onOpenTab: (tab: CapacityPlanTab) => void;
}) => {
  const { depots } = run;
  const [depotIndex, setDepotIndex] = useState(depots[0]?.depotIndex ?? 1);
  const [openRouteIndex, setOpenRouteIndex] = useState<number | null>(null);

  // The heavy query of the feature: one depot's Solves at a time.
  const { data, loading, error } = useCapacityPlanRoutesQuery({
    variables: { runId: run.id, depotIndex },
    errorPolicy: 'all',
  });

  const totals = depotTotalsRow(depots);
  const hasManyDepots = depots.length > 1;
  const depot =
    depots.find((candidate) => candidate.depotIndex === depotIndex) ??
    depots[0];

  // A Planning Depot split into Clusters is solved several times; its routes
  // are one list to the supervisor.
  const solves = data?.capacityPlanRoutes.solves ?? [];
  const routes = solves.flatMap((solve) => solve.routes);
  const unassigned = solves.flatMap((solve) => solve.unassigned);
  const failedSolves = solves.filter(
    (solve) => solve.status === CapacityPlanSolveStatus.Failed,
  );

  const routeRows = toRouteRows(routes, shiftSecondsOf(run));

  const selectDepot = (nextDepotIndex: number) => {
    setDepotIndex(nextDepotIndex);
    setOpenRouteIndex(null);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-5 gap-4">
        <KpiCard
          label="Drivers needed"
          value={totals.drivers}
          valueClassName="text-6xl"
        />
        <KpiCard label="Deliveries planned" value={totals.deliveries} />
        <KpiCard
          label="Recipients per driver"
          value={recipientsPerDriverText(totals)}
        />
        <KpiCard
          label="Unassigned stops"
          value={totals.unassignedStops}
          tone={totals.unassignedStops > 0 ? 'danger' : 'default'}
          sub={
            totals.unassignedStops > 0
              ? `${totals.unassignedDeliveries} deliveries`
              : undefined
          }
        />
        <KpiCard
          label="Computed at"
          value={
            run.computedAt ? formatDateTime(new Date(run.computedAt)) : '—'
          }
        />
      </div>

      {run.outOfCountryDeliveries > 0 && (
        <InfoBox variant="warning" icon={<AlertIcon />}>
          {outOfCountryExcludedNote(run.outOfCountryDeliveries)}. They are
          listed on the Deliveries tab.
        </InfoBox>
      )}

      {run.warnings.length > 0 && (
        <InfoBox variant="warning" icon={<AlertIcon />}>
          <ul className="list-inside list-disc">
            {run.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </InfoBox>
      )}

      {(run.solveProgress.failed > 0 || failedSolves.length > 0) && (
        <FailedSolves
          runFailedCount={run.solveProgress.failed}
          solves={failedSolves}
          failuresAreOnAnotherDepot={hasManyDepots && !loading && !error}
        />
      )}

      {error && (
        <InfoBox variant="danger" icon={<AlertIcon />}>
          The routes of this planning depot could not be loaded. {error.message}
        </InfoBox>
      )}

      {hasManyDepots && (
        <div>
          <h3 className={sectionHeading}>Planning depots</h3>
          <div className="mt-2 flex">
            <GenericTable
              framed
              model={depotSummaryTableModel}
              elements={toDepotSummaryRows(depots)}
              rowKey={(row) => `${row.isTotal ? 'total' : row.depotIndex}`}
              pageNumber={1}
              totalElements={1}
              showPaging={false}
              pageChanged={() => {}}
            />
          </div>
        </div>
      )}

      {/* Above the cards: the picked depot drives them and everything below. */}
      {hasManyDepots && (
        <div className="flex flex-col">
          <label
            className={`text-sm ${subtleText}`}
            htmlFor="capacityPlanDepotSelect"
          >
            Planning depot
          </label>
          <DesignSelect
            inputId="capacityPlanDepotSelect"
            name="capacityPlanDepot"
            className="w-[420px]"
            isMulti={false}
            closeMenuOnSelect
            options={depots.map((candidate) => ({
              value: candidate.depotIndex,
              label: depotOptionLabel(candidate),
            }))}
            value={
              depot
                ? { value: depot.depotIndex, label: depotOptionLabel(depot) }
                : null
            }
            onChange={(option: { value: number } | null) =>
              option && selectDepot(option.value)
            }
          />
        </div>
      )}

      <div className="grid grid-cols-3 gap-4">
        <PerDriverCard
          title="Deliveries per driver"
          noun="deliveries"
          values={routes.map((route) => route.deliveryCount)}
        />
        <PerDriverCard
          title="Stops per driver"
          noun="stops"
          values={routes.map((route) => route.stopCount)}
        />
        <PerDriverCard
          title="Recipients per driver"
          noun="recipients"
          values={routes.map((route) => route.recipients)}
        />
      </div>

      <div>
        <div className="flex items-center gap-4">
          <h3 className={sectionHeading}>Routes</h3>
          <span className={`text-sm ${subtleText}`}>
            Click a driver to open its route and stops
          </span>
          {loading && <span className={`text-xs ${subtleText}`}>Loading…</span>}
        </div>
        <div className="mt-2 flex">
          <GenericTable
            framed
            model={routesTableModel}
            elements={routeRows}
            rowKey={(row) => row.index}
            pageNumber={1}
            totalElements={1}
            showPaging={false}
            pageChanged={() => {}}
            onRowClick={(row) => setOpenRouteIndex(row.index)}
          />
        </div>
      </div>

      <div>
        <h3 className={sectionHeading}>Map</h3>
        <div className="mt-2 h-[480px] w-full overflow-hidden rounded-sm border border-design-secondary-black">
          <ResultMap
            routes={routes}
            depotLatitude={depot?.latitude}
            depotLongitude={depot?.longitude}
          />
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2">
          <h3 className={sectionHeading}>Unassigned stops</h3>
          <span className="rounded-sm bg-muted-white px-1.5 text-xs dark:bg-design-black-accent">
            {unassigned.length}
          </span>
        </div>
        {unassigned.length === 0 ? (
          <p className="mt-2 text-sm">
            {loading || error
              ? 'The stops of this planning depot are not loaded.'
              : 'Every stop was assigned to a driver.'}
          </p>
        ) : (
          <>
            <div className="mt-2 flex">
              <GenericTable
                framed
                model={unassignedTableModel}
                elements={unassigned}
                rowKey={(row) => row.stopId}
                pageNumber={1}
                totalElements={1}
                showPaging={false}
                pageChanged={() => {}}
              />
            </div>
            <div className="mt-2 flex items-center gap-3">
              <span className={`text-xs ${subtleText}`}>
                Fix the coordinates in the Deliveries tab and compute again.
              </span>
              <DesignButton
                variant="ghost"
                size="sm"
                onClickHandler={() => onOpenTab('deliveries')}
              >
                Open Deliveries
              </DesignButton>
            </div>
          </>
        )}
      </div>

      <DriverRouteDialog
        routes={routes}
        routeIndex={openRouteIndex}
        depotLatitude={depot?.latitude}
        depotLongitude={depot?.longitude}
        onSelectRoute={setOpenRouteIndex}
        onClose={() => setOpenRouteIndex(null)}
      />
    </div>
  );
};

/**
 * The Result of one Run. Compute is started from the run header, so this tab
 * only reports: the wait, the failure, or the plan.
 */
const ResultTab = ({ run }: ResultTabProps) => {
  const { setTab } = useCapacityPlanUrl();

  if (isRunSolving(run.status)) {
    return <ProgressPanel run={run} />;
  }

  if (run.status === CapacityPlanRunStatus.Failed) {
    return <FailedPanel run={run} onOpenTab={setTab} />;
  }

  if (run.status === CapacityPlanRunStatus.Computed && run.depots.length > 0) {
    return (
      <ComputedResult
        // A fresh result means a fresh selection, so the panel starts over.
        key={`${run.id}-${run.computedAt}`}
        run={run}
        onOpenTab={setTab}
      />
    );
  }

  return (
    <InfoBox variant="default" icon={<InfoIcon />}>
      {run.pendingGeocodes > 0
        ? 'This run has not been computed yet. Use Compute plan in the run header once geocoding finishes.'
        : 'This run has not been computed yet. Use Compute plan in the run header.'}
    </InfoBox>
  );
};

export default ResultTab;
